import axios from 'axios';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { renderEmail } from 'react-html-email';
import { Cookies, cookieKeys, cookieConfig } from './auth/cookie_utils';

const baseURL = `${process.env.REACT_APP_BACKEND_HOST}:${process.env.REACT_APP_BACKEND_PORT}`;

const TLPBackend = axios.create({
  baseURL,
  withCredentials: true,
});

// Converts JS Date object into string, formatted MM/DD/YYYY
const formatDate = value => {
  return value.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

// capitalizes first letter of a string
export const capitalize = s => {
  return s[0].toUpperCase() + s.slice(1);
};

// formats school year into startYear-endYear where endYear is one year later
export const formatSchoolYear = startYear => {
  return `${startYear}-${startYear - 1999}`;
};

// TLP's data starts with the 2020-2021 school year, so every dropdown starts there
export const FIRST_SCHOOL_YEAR = 2020;

// The one school year list. Every dropdown reads from this instead of keeping
// its own hard-coded array, which is how they drifted apart in the first place.
// Start years run from 2020 through the year after the school year in progress,
// so a group can be set up for next fall before this one ends. The year rolls
// in July (getMonth() zero-based, so July is 6) -- the same getMonth() >= 6
// convention the master teacher dashboard already used.
// format 'long' gives '2025-2026', 'short' gives '2025-26' (formatSchoolYear).
// Both keep the start year in the first four characters, which is what callers
// parse back out with Number(choice.slice(0, 4)).
export const getSchoolYearChoices = ({ format = 'long', now = new Date() } = {}) => {
  const lastStartYear = now.getFullYear() + (now.getMonth() >= 6 ? 1 : 0);
  const count = lastStartYear - FIRST_SCHOOL_YEAR + 1;
  return Array.from({ length: count }, (unused, index) => {
    const startYear = FIRST_SCHOOL_YEAR + index;
    return format === 'short' ? formatSchoolYear(startYear) : `${startYear}-${startYear + 1}`;
  });
};

// A student group can exist before its site is staffed, so the backend takes a
// master teacher id or null and nothing else: '' is a malformed foreign key
// (400) and 0 is not a user (foreign key violation). Anything that is not a
// real id goes over the wire as null, which reads as "no teacher yet".
export const toMasterTeacherId = value => {
  const id = typeof value === 'string' ? Number(value.trim()) : value;
  return Number.isInteger(id) && id > 0 ? id : null;
};

export const formatPhoneNumber = phoneNumber => {
  if (phoneNumber.length === 10) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  }
  return phoneNumber;
};

// turns military time HH:MM:SS into standard time HH:MM AA
export const parseTime = dateString => {
  // eslint-disable-next-line prefer-const
  let [hour, minute] = dateString.split(':');
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = ((Number(hour) + 11) % 12) + 1;
  return `${hour}:${minute} ${suffix}`;
};

export const passwordRulesTooltipText =
  '\u2022 Must be at least 8 characters long' +
  '<br />' +
  '\u2022 Include at least one uppercase and lowercase letter' +
  '<br />' +
  '\u2022 Include at least 1 number' +
  '<br />' +
  '\u2022 Include one of these set of special characters [@$!%*?&]';

export const passwordRegExp =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,255}$/;

// calculate the attitudinal and academic pre and post average scores
// returns null if there is not at least pre-scores for other categories
// otherwise return object with pre and post scores
export const calculateScores = data => {
  const scores = {};
  const attitudinalPossible = 80;
  const academicPossible = 93;
  const attitudinal = {
    pre: 0,
    preCount: 0,
    post: 0,
    postCount: 0,
  };
  const academic = {
    pre: 0,
    preCount: 0,
    post: 0,
    postCount: 0,
  };

  // remains false if no students in data has non-null pretest scores
  let hasData = false;

  data.forEach(student => {
    // if array is not null, get sum and divide by corresponding total possible
    if (student.pretestR !== null) {
      hasData = true;
      attitudinal.pre +=
        (student.pretestR.reduce((prev, curr) => prev + curr, 0) / attitudinalPossible) * 100;
      attitudinal.preCount += 1;

      // only get student's post score if they have pre-scores
      if (student.posttestR !== null) {
        attitudinal.post +=
          (student.posttestR.reduce((prev, curr) => prev + curr, 0) / attitudinalPossible) * 100;
        attitudinal.postCount += 1;
      }
    }

    if (student.pretestA !== null) {
      hasData = true;
      academic.pre +=
        (student.pretestA.reduce((prev, curr) => prev + curr, 0) / academicPossible) * 100;
      academic.preCount += 1;

      if (student.posttestA !== null) {
        academic.post +=
          (student.posttestA.reduce((prev, curr) => prev + curr, 0) / academicPossible) * 100;
        academic.postCount += 1;
      }
    }
  });

  if (hasData) {
    // if counts are 0, return scores of 0. otherwise, calculate average
    scores.pre = [
      attitudinal.preCount ? attitudinal.pre / attitudinal.preCount : 0,
      academic.preCount ? academic.pre / academic.preCount : 0,
    ];
    scores.post = [
      attitudinal.postCount ? attitudinal.post / attitudinal.postCount : 0,
      academic.postCount ? academic.post / academic.postCount : 0,
    ];
  }
  return scores;
};

// Calculate the attitudinal and academic pre and post average scores
// for a single student. Returns object with calculated scores
export const calculateSingleStudentScores = data => {
  const MAX_ASSESSMENT_SCORE = 93;
  const MAX_ATTITUDE_SCORE = 80;

  const preAttitude = (data?.pretestR?.reduce((tot, a) => tot + a, 0) / MAX_ATTITUDE_SCORE) * 100;
  const postAttitude = (data?.posttestR?.reduce((tot, a) => tot + a, 0) / MAX_ATTITUDE_SCORE) * 100;
  const preAssessment =
    (data?.pretestA?.reduce((tot, a) => tot + a, 0) / MAX_ASSESSMENT_SCORE) * 100;
  const postAssessment =
    (data?.posttestA?.reduce((tot, a) => tot + a, 0) / MAX_ASSESSMENT_SCORE) * 100;

  return {
    preAttitude,
    postAttitude,
    preAssessment,
    postAssessment,
  };
};

const average = arr => {
  return arr.reduce((a, b) => a + b) / arr.length;
};

// calculates the pre and post average of attitudinal + academic for given site and all other TLP sites
// returns an object with two arrays (pre and post)
// assumes that site has both pre and post data
export const calculateSiteScores = (site, other) => {
  const scores = {};
  scores.pre = [average(site.pre), other.pre ? average(other.pre) : 0];
  scores.post = [average(site.post), other.post ? average(other.post) : 0];
  return scores;
};

export const sendEmail = async (email, htmlMessage) => {
  try {
    await TLPBackend.post('/send-email', { email, htmlMessage: renderEmail(htmlMessage) });
  } catch (err) {
    throw new Error(err.message);
  }
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

// Using Firebase Web version 9
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTHDOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECTID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGEBUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGINGSENDERID,
  appId: process.env.REACT_APP_FIREBASE_APPID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const refreshUrl = `https://securetoken.googleapis.com/v1/token?key=${process.env.REACT_APP_FIREBASE_APIKEY}`;

/**
 * Sets a cookie in the browser
 * @param {string} key key for the cookie
 * @param {string} value value for the cookie
 * @param {cookieConfig} config cookie config to use
 */
const setCookie = (key, value, config) => {
  let cookie = `${key}=${value}; max-age=${config.maxAge}; path=${config.path}`;
  if (config.domain) {
    cookie += `; domain=${config.domain}`;
  }
  if (config.secure) {
    cookie += '; secure';
  }
  document.cookie = cookie;
};

/**
 * Returns the current user synchronously
 * @param {Auth} authInstance
 * @returns The current user (or undefined)
 */
const getCurrentUser = authInstance =>
  new Promise((resolve, reject) => {
    const unsubscribe = authInstance.onAuthStateChanged(
      user => {
        unsubscribe();
        resolve(user);
      },
      err => {
        reject(err);
      },
    );
  });

// Refreshes the current user's access token by making a request to Firebase
export const refreshToken = async () => {
  const currentUser = await getCurrentUser(auth);
  if (currentUser) {
    const refreshT = currentUser.refreshToken;
    const {
      data: { access_token: idToken },
    } = await axios.post(refreshUrl, {
      grant_type: 'refresh_token',
      refresh_token: refreshT,
    });
    // Sets the appropriate cookies after refreshing access token
    setCookie(cookieKeys.ACCESS_TOKEN, idToken, cookieConfig);
    const user = await TLPBackend.get(`/tlp-users/${auth.currentUser.uid}`);
    setCookie(cookieKeys.POSITION, user.data.position, cookieConfig);
    setCookie(cookieKeys.USER_ID, user.data.userId, cookieConfig);
    return idToken;
  }
  return null;
};

/**
 * Resolves the Firebase ID token to send with a backend request.
 *
 * The portal and the API live on different subdomains, so the login cookie is
 * host-only to the portal and never reaches the API. The bearer header is what
 * actually carries the session. getIdToken() refreshes on its own when the token
 * is close to expiring. The cookie is only a fallback for the moment right after
 * a cold page load, before Firebase has restored the session.
 *
 * @returns {Promise<string|null>} the ID token, or null if nobody is signed in
 */
const getAuthToken = async () => {
  try {
    const currentUser = auth.currentUser || (await getCurrentUser(auth));
    if (currentUser) {
      return await currentUser.getIdToken();
    }
  } catch (err) {
    // fall through to the cookie
  }
  return new Cookies().get(cookieKeys.ACCESS_TOKEN) || null;
};

/**
 * Attaches the Firebase ID token to every backend request as a bearer token.
 * @param {AxiosInstance} axiosInstance instance of axios to apply interceptor to
 */
const addAuthHeaderInterceptor = axiosInstance => {
  axiosInstance.interceptors.request.use(async config => {
    const token = await getAuthToken();
    if (token) {
      // eslint-disable-next-line no-param-reassign
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }
    return config;
  });
};

/**
 * Adds an axios interceptor for auth to given axiosInstance
 * @param {AxiosInstance} axiosInstance instance of axios to apply interceptor to
 */
const addAuthInterceptor = axiosInstance => {
  // This response interceptor will refresh the user's access token using the refreshToken helper method
  axiosInstance.interceptors.response.use(
    response => {
      return response;
    },
    async error => {
      if (error.response) {
        const { status, data } = error.response;
        const originalConfig = error.response.config || {};
        switch (status) {
          // The backend answers 401 for a missing or expired token. 400 is the
          // legacy status and is kept so an older backend still triggers a refresh.
          case 400:
          case 401:
            // check if the error was the token, and only retry once
            if (data === '@verifyToken no access token' && !originalConfig.tlpRetried) {
              // token has expired;
              try {
                // attempting to refresh token;
                const idToken = await refreshToken();
                // token refreshed, reattempting request;
                const config = originalConfig;
                config.tlpRetried = true;
                // configure new request in a new instance;
                return await axios({
                  method: config.method,
                  url: `${config.baseURL}${config.url}`,
                  data: config.data,
                  params: config.params,
                  headers: {
                    ...config.headers,
                    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
                  },
                  withCredentials: true,
                });
              } catch (e) {
                return Promise.reject(e);
              }
            } else {
              return Promise.reject(error);
            }
          default:
            return Promise.reject(error);
        }
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        return Promise.reject(error);
      } else {
        // Something happened in setting up the request that triggered an Error
        return Promise.reject(error);
      }
    },
  );
};

const reloadPage = () => window.location.reload();

addAuthHeaderInterceptor(TLPBackend);
addAuthInterceptor(TLPBackend);

export { auth, TLPBackend, formatDate, reloadPage, scrollToTop };
